import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { employmentService } from '../../../../services/employment';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('EmploymentInfoInput');

type EmploymentInfoInputScreenRouteProp = RouteProp<RootStackParamList, 'EmploymentInfoInput'>;

export const EmploymentInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<EmploymentInfoInputScreenRouteProp>();

  const { employmentData, isEditing, isRetired } = route.params || {};
  const [retirementMode, setRetirementMode] = useState(isRetired || employmentData?.employmentType === 'Retired' || false);

  // Helper function to format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;

    // Limit to 10 digits
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Helper function to format date as MM/DD/YYYY
  const formatDate = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;

    // Limit to 8 digits (MMDDYYYY)
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  // Helper function to validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to format salary as $x,xxx,xxx
  const formatSalary = (value: string) => {
    // Remove all non-numeric characters except decimal point
    const cleaned = value.replace(/[^\d.]/g, '');

    // Handle decimal point
    const parts = cleaned.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1];

    // Add commas to integer part
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Combine with decimal if present
    let formatted = integerPart;
    if (decimalPart !== undefined) {
      // Limit decimal to 2 places
      formatted += '.' + decimalPart.slice(0, 2);
    }

    // Add dollar sign
    return formatted ? '$' + formatted : '';
  };

  // Employment type options
  const employmentTypes = [
    'Full-time',
    'Part-time',
    'Contract',
    'Temporary',
    'Intern',
    'Consultant',
    'Freelance',
    'Other'
  ];

  // Helper function to check if all required emails are valid
  const areEmailsValid = (): boolean => {
    const emails = [workEmail, supervisorEmail, hrContactEmail];
    return emails.every(email => email === '' || validateEmail(email));
  };

  const [companyName, setCompanyName] = useState(employmentData?.companyName || '');
  const [jobTitle, setJobTitle] = useState(employmentData?.jobTitle || '');
  const [workAddress, setWorkAddress] = useState(employmentData?.workAddress || '');
  const [workPhone, setWorkPhone] = useState(employmentData?.workPhone || '');
  const [workEmail, setWorkEmail] = useState(employmentData?.workEmail || '');
  const [supervisorName, setSupervisorName] = useState(employmentData?.supervisorName || '');
  const [supervisorPhone, setSupervisorPhone] = useState(employmentData?.supervisorPhone || '');
  const [supervisorEmail, setSupervisorEmail] = useState(employmentData?.supervisorEmail || '');
  const [hrContactName, setHrContactName] = useState(employmentData?.hrContactName || '');
  const [hrContactPhone, setHrContactPhone] = useState(employmentData?.hrContactPhone || '');
  const [hrContactEmail, setHrContactEmail] = useState(employmentData?.hrContactEmail || '');
  const [employeeId, setEmployeeId] = useState(employmentData?.employeeId || '');
  const [salary, setSalary] = useState(
    employmentData?.salary ? formatSalary(employmentData.salary.toString()) : ''
  );
  const [employmentType, setEmploymentType] = useState(employmentData?.employmentType || '');
  const [benefitsInfo, setBenefitsInfo] = useState(employmentData?.benefitsInfo || '');
  const [additionalNotes, setAdditionalNotes] = useState(employmentData?.additionalNotes || '');
  const [retirementDate, setRetirementDate] = useState(employmentData?.retirementDate || '');
  const [hasPension, setHasPension] = useState(employmentData?.hasPension || false);
  const [pensionDetails, setPensionDetails] = useState(employmentData?.pensionDetails || '');
  const [saving, setSaving] = useState(false);
  const [showEmploymentTypeDropdown, setShowEmploymentTypeDropdown] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedEmploymentId, setSavedEmploymentId] = useState<string | undefined>();

  const handleSave = async () => {
    // Validation - require at least company name
    if (!companyName.trim()) {
      Alert.alert('Error', 'Please enter at least company name');
      return;
    }

    // Validate emails before saving
    if (!areEmailsValid()) {
      Alert.alert('Invalid Email', 'Please check that all email addresses are in the correct format (e.g., user@example.com)');
      return;
    }

    try {
      setSaving(true);

      const employmentUpdateData = {
        taskId: 2, // Employment info task ID
        companyName: companyName.trim(),
        jobTitle: jobTitle.trim(),
        workAddress: workAddress.trim(),
        workPhone: workPhone.trim(),
        workEmail: workEmail.trim(),
        supervisorName: supervisorName.trim(),
        supervisorPhone: supervisorPhone.trim(),
        supervisorEmail: supervisorEmail.trim(),
        hrContactName: hrContactName.trim(),
        hrContactPhone: hrContactPhone.trim(),
        hrContactEmail: hrContactEmail.trim(),
        employeeId: employeeId.trim(),
        salary: salary ? parseFloat(salary.replace(/[$,]/g, '')) : undefined,
        employmentType: retirementMode ? 'Retired' : employmentType.trim(),
        benefitsInfo: benefitsInfo.trim(),
        additionalNotes: additionalNotes.trim(),
        retirementDate: retirementMode ? retirementDate.trim() : undefined,
        hasPension: retirementMode ? hasPension : undefined,
        pensionDetails: retirementMode && hasPension ? pensionDetails.trim() : undefined,
      };

      let savedEmployment;

      if (isEditing && employmentData?.id) {
        // Update existing record
        savedEmployment = await employmentService.updateEmployment(
          employmentData.id,
          employmentUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedEmployment?.data || savedEmployment;
        logger.debug('Employment updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('EmploymentLockUnlock', { employmentId: updatedData.id });
      } else {
        // Create new record
        savedEmployment = await employmentService.createEmployment(employmentUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedEmployment?.data || savedEmployment;
        logger.debug('Employment saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedEmploymentId(createdData.id);

        // Clear the form and reset states for Add Another
        setCompanyName('');
        setJobTitle('');
        setWorkAddress('');
        setWorkPhone('');
        setWorkEmail('');
        setSupervisorName('');
        setSupervisorPhone('');
        setSupervisorEmail('');
        setHrContactName('');
        setHrContactPhone('');
        setHrContactEmail('');
        setEmployeeId('');
        setSalary('');
        setEmploymentType('');
        setBenefitsInfo('');
        setAdditionalNotes('');
        setRetirementDate('');
        setHasPension(false);
        setPensionDetails('');

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving employment:', error);
      Alert.alert('Error', 'Failed to save employment information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setCompanyName('');
    setJobTitle('');
    setWorkAddress('');
    setWorkPhone('');
    setWorkEmail('');
    setSupervisorName('');
    setSupervisorPhone('');
    setSupervisorEmail('');
    setHrContactName('');
    setHrContactPhone('');
    setHrContactEmail('');
    setEmployeeId('');
    setSalary('');
    setEmploymentType('');
    setBenefitsInfo('');
    setAdditionalNotes('');
    setRetirementDate('');
    setHasPension(false);
    setPensionDetails('');
    setShowAddAnotherDone(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedEmploymentId);

    // If we don't have a saved ID, get the most recent employment entry
    if (!savedEmploymentId) {
      try {
        const response = await employmentService.getAllEmployment();
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allEmployment = response?.data || response;
        logger.debug('[Done Debug] Extracted employment array:', allEmployment);

        const mostRecent = allEmployment && Array.isArray(allEmployment) && allEmployment.length > 0 ? allEmployment[allEmployment.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('EmploymentLockUnlock', { employmentId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No employment entries found in array:', allEmployment);
          Alert.alert('Error', 'No employment information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting employment:', error);
        Alert.alert('Error', 'Failed to get employment information.');
      }
    } else {
      navigation.navigate('EmploymentLockUnlock', { employmentId: savedEmploymentId });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          {retirementMode ? 'What is your Retirement Information?' : 'What is your Employment Information?'}
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          {/* Toggle between Employment and Retirement */}
          <View style={styles.toggleContainer}>
            <TouchableOpacity
              style={[styles.toggleButton, !retirementMode && styles.toggleButtonActive]}
              onPress={() => setRetirementMode(false)}
            >
              <Text style={[styles.toggleText, !retirementMode && styles.toggleTextActive]}>
                Currently Employed
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toggleButton, retirementMode && styles.toggleButtonActive]}
              onPress={() => setRetirementMode(true)}
            >
              <Text style={[styles.toggleText, retirementMode && styles.toggleTextActive]}>
                Retired
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{retirementMode ? 'Former Company Name *' : 'Company Name *'}</Text>
            <TextInput
              style={styles.input}
              value={companyName}
              onChangeText={setCompanyName}
              placeholder="Ex: Acme Corporation"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Job Title</Text>
            <TextInput
              style={styles.input}
              value={jobTitle}
              onChangeText={setJobTitle}
              placeholder="Ex: Software Engineer"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Work Address</Text>
            <TextInput
              style={styles.input}
              value={workAddress}
              onChangeText={setWorkAddress}
              placeholder="123 Main St, City, State ZIP"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Work Phone Number</Text>
            <TextInput
              style={styles.input}
              value={workPhone}
              onChangeText={(text) => setWorkPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Work Email</Text>
            <TextInput
              style={[
                styles.input,
                workEmail && !validateEmail(workEmail) && styles.inputError
              ]}
              value={workEmail}
              onChangeText={setWorkEmail}
              placeholder="user@company.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
            {workEmail && !validateEmail(workEmail) && (
              <Text style={styles.errorText}>Please enter a valid email address</Text>
            )}
          </View>

          {/* Only show employment-specific fields when NOT retired */}
          {!retirementMode && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Supervisor Name</Text>
                <TextInput
                  style={styles.input}
                  value={supervisorName}
                  onChangeText={setSupervisorName}
                  placeholder="Ex: John Smith"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Supervisor Phone</Text>
                <TextInput
                  style={styles.input}
                  value={supervisorPhone}
                  onChangeText={(text) => setSupervisorPhone(formatPhoneNumber(text))}
                  placeholder="(xxx) xxx-xxxx"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                  maxLength={14}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Supervisor Email</Text>
                <TextInput
                  style={[
                    styles.input,
                    supervisorEmail && !validateEmail(supervisorEmail) && styles.inputError
                  ]}
                  value={supervisorEmail}
                  onChangeText={setSupervisorEmail}
                  placeholder="supervisor@company.com"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
                {supervisorEmail && !validateEmail(supervisorEmail) && (
                  <Text style={styles.errorText}>Please enter a valid email address</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>HR Contact Name</Text>
                <TextInput
                  style={styles.input}
                  value={hrContactName}
                  onChangeText={setHrContactName}
                  placeholder="Ex: Jane Doe"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>HR Contact Phone</Text>
                <TextInput
                  style={styles.input}
                  value={hrContactPhone}
                  onChangeText={(text) => setHrContactPhone(formatPhoneNumber(text))}
                  placeholder="(xxx) xxx-xxxx"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                  maxLength={14}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>HR Contact Email</Text>
                <TextInput
                  style={[
                    styles.input,
                    hrContactEmail && !validateEmail(hrContactEmail) && styles.inputError
                  ]}
                  value={hrContactEmail}
                  onChangeText={setHrContactEmail}
                  placeholder="hr@company.com"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
                {hrContactEmail && !validateEmail(hrContactEmail) && (
                  <Text style={styles.errorText}>Please enter a valid email address</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Employee ID</Text>
                <TextInput
                  style={styles.input}
                  value={employeeId}
                  onChangeText={setEmployeeId}
                  placeholder="Ex: EMP12345"
                  placeholderTextColor="#A9A9A9"
                />
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{retirementMode ? 'Former Annual Salary' : 'Annual Salary'}</Text>
            <TextInput
              style={styles.input}
              value={salary}
              onChangeText={(text) => setSalary(formatSalary(text))}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          {/* Retirement-specific fields */}
          {retirementMode && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Retirement Date</Text>
                <TextInput
                  style={styles.input}
                  value={retirementDate}
                  onChangeText={(text) => setRetirementDate(formatDate(text))}
                  placeholder="MM/DD/YYYY"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="numeric"
                  maxLength={10}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Do you have a pension?</Text>
                <View style={styles.toggleContainer}>
                  <TouchableOpacity
                    style={[styles.toggleButton, hasPension && styles.toggleButtonActive]}
                    onPress={() => setHasPension(true)}
                  >
                    <Text style={[styles.toggleText, hasPension && styles.toggleTextActive]}>Yes</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.toggleButton, !hasPension && styles.toggleButtonActive]}
                    onPress={() => setHasPension(false)}
                  >
                    <Text style={[styles.toggleText, !hasPension && styles.toggleTextActive]}>No</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {hasPension && (
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Pension Details</Text>
                  <TextInput
                    style={[styles.input, styles.textArea]}
                    value={pensionDetails}
                    onChangeText={setPensionDetails}
                    placeholder="Provider, amount, start date, etc."
                    placeholderTextColor="#A9A9A9"
                    multiline
                    numberOfLines={4}
                  />
                </View>
              )}
            </>
          )}

          {!retirementMode && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Employment Type</Text>
            <TouchableOpacity
              style={styles.dropdownContainer}
              onPress={() => setShowEmploymentTypeDropdown(!showEmploymentTypeDropdown)}
            >
              <Text style={[!employmentType ? styles.dropdownPlaceholder : styles.dropdownText]}>
                {employmentType || 'Select employment type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownArrow}
              />
            </TouchableOpacity>

            {showEmploymentTypeDropdown && (
              <View style={styles.dropdownList}>
                {employmentTypes.map((type) => (
                  <TouchableOpacity
                    key={type}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setEmploymentType(type);
                      setShowEmploymentTypeDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownItemText}>{type}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{retirementMode ? 'Former Benefits Information' : 'Benefits Information'}</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={benefitsInfo}
              onChangeText={setBenefitsInfo}
              placeholder="Health insurance, retirement plans, vacation days, etc."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={additionalNotes}
              onChangeText={setAdditionalNotes}
              placeholder="Any additional employment information"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {saving ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSave}
                >
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  toggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#E5E5E5',
    borderRadius: ms(25),
    padding: ms(4),
    marginBottom: ms(20),
  },
  toggleButton: {
    flex: 1,
    paddingVertical: ms(12),
    borderRadius: ms(22),
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#29BFC0',
  },
  toggleText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  toggleTextActive: {
    color: '#FFFFFF',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownContainer: {
    backgroundColor: '#FFFFFF',
    borderColor: '#d8dbdd',
    borderWidth: ms(1),
    borderRadius: ms(25),
    paddingHorizontal: ms(15),
    height: s(50),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  dropdownPlaceholder: {
    fontSize: ms(16, 0.3),
    color: '#A9A9A9',
  },
  dropdownArrow: {
    width: s(24),
    height: s(24),
    tintColor: '#6B7280',
  },
  dropdownList: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    marginTop: ms(5),
    marginBottom: ms(20),
    maxHeight: s(200),
    overflow: 'hidden',
  },
  dropdownItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#F3F4F6',
  },
  dropdownItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  inputError: {
    borderColor: '#FF4444',
    borderWidth: ms(2),
  },
  errorText: {
    color: '#FF4444',
    fontSize: ms(14, 0.3),
    marginTop: ms(-10),
    marginBottom: ms(10),
    marginLeft: ms(15),
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
}); 